// PrimeNG `Preset` is opaque; we narrow our extension/preset records into it at the boundary.

import {
  computed,
  effect,
  inject,
  InjectionToken,
  untracked,
} from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { updatePreset, updateSurfacePalette } from '@primeuix/themes';
import type { Preset } from '@primeuix/themes/types';
import {
  THEME_BRAND_TOKENS,
  type ThemeBrandTokens,
} from './theme.brand-tokens';
import { INSTRUMENT_SURFACE_PALETTE } from './theme.palettes';
import { getInitialState, persistState } from './theme.persistence';
import { buildPresetExtension, getPrimitive } from './theme.presets';
import { ELECTRIC_STATUS_PALETTES } from './theme.secondaries';
import type { MenuMode, ThemeState } from './theme.types';

/**
 * Optional map of named custom color palettes injected by the brand layer.
 * Keys become available as PrimeNG primitives (e.g. `indutrust`, `brand-a`).
 * Defaults to an empty record so shared libs remain brand-agnostic.
 */
export const THEME_BRAND_PALETTES = new InjectionToken<
  Record<string, Record<string, string>>
>('THEME_BRAND_PALETTES', { factory: () => ({}) });

function addDarkModeClass(): void {
  document.documentElement.classList.add('app-dark');
}

function removeDarkModeClass(): void {
  document.documentElement.classList.remove('app-dark');
}

function isDarkModeClassApplied(): boolean {
  return document.documentElement.classList.contains('app-dark');
}

function withDarkModeViewTransition(toggle: () => void): void {
  if ('startViewTransition' in document) {
    (
      document as Document & {
        startViewTransition: (cb: () => void) => void;
      }
    ).startViewTransition(toggle);
  } else {
    toggle();
  }
}

function enableDarkModeWithViewTransition(): void {
  withDarkModeViewTransition(addDarkModeClass);
}

function disableDarkModeWithViewTransition(): void {
  withDarkModeViewTransition(removeDarkModeClass);
}

function resolveBrandPrimaryName(
  brandTokens: ThemeBrandTokens | null,
  brandPalettes: Record<string, Record<string, string>>
): string {
  if (brandTokens !== null) {
    return brandTokens.primaryName;
  }
  const keys = Object.keys(brandPalettes);
  return keys.length > 0 ? keys[0] : 'indutrust';
}

/**
 * Signal store managing application theming: authored brand identity, dark
 * mode, and menu mode.
 *
 * Only `darkTheme` and `menuMode` are user-controlled (persisted under
 * `idt-theme`). Preset / primary / surface are authored by the brand layer and
 * re-applied on every boot so a leftover rainbow-picker value cannot override
 * the designed look. On first visit (no saved boolean `darkTheme`), dark mode
 * is initialized from `prefers-color-scheme`; that snapshot is written on init
 * and later OS changes are ignored (no live "system" mode).
 *
 * @example
 * ```typescript
 * import { inject } from '@angular/core';
 * import { ThemeStore } from '@indutrust/shared-data';
 *
 * export class SomeComponent {
 *   readonly themeStore = inject(ThemeStore);
 *
 *   toggleDark() {
 *     this.themeStore.toggleDarkMode();
 *   }
 * }
 * ```
 */
export const ThemeStore = signalStore(
  { providedIn: 'root' },
  withState(() => {
    const brandTokens = inject(THEME_BRAND_TOKENS);
    const brandPalettes = inject(THEME_BRAND_PALETTES);
    return getInitialState(resolveBrandPrimaryName(brandTokens, brandPalettes));
  }),
  withComputed((store) => ({
    isDarkTheme: computed(() => store.darkTheme()),
    isOverlay: computed(() => store.menuMode() === 'overlay'),
  })),
  withMethods((store) => ({
    setDarkMode(dark: boolean) {
      patchState(store, { darkTheme: dark });
    },

    setMenuMode(mode: MenuMode) {
      patchState(store, { menuMode: mode });
    },

    toggleDarkMode() {
      patchState(store, {
        darkTheme: !store.darkTheme(),
      });
    },
  })),
  withHooks((store) => {
    const brandPalettes = inject(THEME_BRAND_PALETTES);
    const brandTokens = inject(THEME_BRAND_TOKENS);

    return {
      onInit() {
        const primaryName = resolveBrandPrimaryName(brandTokens, brandPalettes);
        const primaryPalette =
          brandTokens !== null
            ? brandTokens.primary
            : (brandPalettes[primaryName] ??
              getPrimitive(store.preset(), brandPalettes)?.[primaryName]);
        const instrumentPalette =
          brandTokens !== null
            ? brandTokens.instrument
            : INSTRUMENT_SURFACE_PALETTE;

        // Force authored identity even if an old localStorage blob had
        // emerald / Lara / zinc from the retired configurator.
        patchState(store, {
          preset: 'Aura',
          primaryColor: primaryName,
          surfaceColor: 'instrument',
        });

        const ext = {
          ...buildPresetExtension('Aura', primaryName, primaryPalette),
          primitive: ELECTRIC_STATUS_PALETTES,
        } as Preset;
        updatePreset(ext);

        updateSurfacePalette(instrumentPalette);

        if (store.darkTheme()) {
          addDarkModeClass();
        }

        // React to dark mode changes with view transition API. Skip when the
        // document already matches: the initial state is applied above, and a
        // view transition on first paint only makes the page non-interactive
        // (and untappable) while it runs.
        effect(() => {
          const darkTheme = store.darkTheme();

          untracked(() => {
            if (darkTheme === isDarkModeClassApplied()) {
              return;
            }

            if (darkTheme) {
              enableDarkModeWithViewTransition();
            } else {
              disableDarkModeWithViewTransition();
            }
          });
        });

        // Persist state to localStorage on any change
        effect(() => {
          const state: ThemeState = {
            preset: store.preset(),
            primaryColor: store.primaryColor(),
            surfaceColor: store.surfaceColor(),
            darkTheme: store.darkTheme(),
            menuMode: store.menuMode(),
          };
          untracked(() => persistState(state));
        });
      },
    };
  })
);
